import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { AppState } from '../../../app-state/app.state';
import { ScandataService } from '../../../scandata/scandata.service';
import { ALLOWED_EXTENSIONS, ImportFile } from '../../import.models';
import { allowImportForFileType } from '../../import.service';
import { PickerType } from '../file-picker.models';

declare const Dropbox: Dropbox.Chooser & { appKey: string };

@Injectable({
  providedIn: 'root',
})
export class DropboxFilePickerService {
  private readonly store = inject(Store);
  private readonly scandataService = inject(ScandataService);

  private get dropboxSettings() {
    return this.store.selectSnapshot(AppState.settings).dropbox;
  }

  show(): Observable<ImportFile[]> {
    return from(this.openPicker()).pipe(
      switchMap((files) => {
        if (files.length === 0) return of([]);

        const files$ = files.map((file) => this.mapToImportFile(file));
        return forkJoin(files$);
      }),
    );
  }

  private openPicker(): Promise<Dropbox.ChooserFile[]> {
    return new Promise((resolve) => {
      Dropbox.appKey = this.dropboxSettings.appKey;
      Dropbox.choose({
        linkType: 'direct',
        multiselect: true,
        folderselect: false,
        extensions: ALLOWED_EXTENSIONS,
        success: (result: Dropbox.ChooserFile[]) => {
          const files = result.filter((file) => allowImportForFileType(file.name));
          resolve(files);
        },
        cancel: () => resolve([]),
      });
    });
  }

  private mapToImportFile(file: Dropbox.ChooserFile): Observable<ImportFile> {
    const externalFileId = `dropbox-${file.id}-${file.bytes}`;
    const alreadyIngested$ = this.scandataService
      .getScansByQuery({ filters: { externalFileId } })
      .pipe(map((scans) => scans.length > 0));

    return alreadyIngested$.pipe(
      map((alreadyIngested) => ({
        id: file.id,
        name: file.name,
        size: file.bytes,
        pickerType: PickerType.Dropbox,
        downloadUrl: file.link,
        externalFileId,
        alreadyIngested,
      })),
    );
  }
}
